import menuItems from "../data/menuItems.json";
import { AccessControl } from "../redux/actions/authActions";

/**
 * Where a signed-in user should land.
 *
 * There used to be a Dashboard placeholder at /dashboard, and it was the target
 * of the post-login redirect, the catch-all and the sidebar's first entry. It
 * showed nothing but a "coming soon" card, so every user's first screen after
 * signing in was an empty one. The screen is gone; this decides what to open
 * instead.
 *
 * It CANNOT be a fixed path. Nearly every other route is gated by a permission
 * flag from the login response, so hardcoding, say, /catalogue/programs would
 * land a meals-only account on "You don't have access to this page" — worse than
 * the placeholder it replaced. Instead this walks the sidebar in its display
 * order and returns the first entry that account can actually open, which is the
 * same thing they would click first anyway.
 */

/** Sidebar entry id -> the permission flag that gates it. null = always visible. */
export const MENU_ACCESS_MAP: Record<string, keyof AccessControl | null> = {
  catalogue: "catalogue",
  contacts: "customer_access",
  "sales-marketing": "marketing",
  "booking-orders": "booking",
  calendar: null,
  // No access flag exists for ABHA, and /abha carries no RequireAccess guard,
  // so the entry follows the route: visible to anyone signed in.
  abha: null,
  remnutriMeals: "meals",
  // No access flag covers the console's own user accounts, so this follows the
  // ABHA precedent above: visible to anyone signed in, matching its route. Swap
  // in a real flag once the backend defines one.
  "user-history": null,
  settings: "settings",
  "help-support": null,
};

/**
 * Last resort when an account can open nothing in the sidebar.
 *
 * /profile is the only authed screen with neither a permission gate nor a
 * sidebar entry, so it is reachable no matter how empty the access object is.
 * Landing somewhere real beats bouncing off the catch-all.
 */
export const FALLBACK_LANDING_PATH = "/profile";

interface MenuEntry {
  id: string;
  path: string;
  subItems?: { id: string; name: string; path: string }[];
}

/**
 * The path a sidebar entry actually opens.
 *
 * Entries with a dropdown are not always routable themselves — "RemNutri Meals"
 * carries `/remnutriMeals`, which has no route, so navigating there would fall
 * through to the catch-all and bounce straight back here. Their first child is
 * the real destination, which is also what the sidebar opens when collapsed.
 */
export const menuItemPath = (item: MenuEntry): string =>
  item.subItems && item.subItems.length > 0 ? item.subItems[0].path : item.path;

/**
 * True when `access` permits this sidebar entry. Unmapped ids are visible.
 *
 * `grants` — the per-user list from Settings → Authorization — overrides the
 * role flags whenever it is present, including for the entries no flag covers
 * (ABHA, Calendar, User History). Null means no per-user grid applies.
 */
export const canOpenMenuItem = (
  id: string,
  access: AccessControl | null,
  grants: string[] | null = null
): boolean => {
  if (grants) return grants.includes(id);
  const flag = MENU_ACCESS_MAP[id];
  if (flag === null) return true;
  if (flag === undefined) return true;
  return access?.[flag] === true;
};

/** Sub-items are only narrowed by the per-user grid; role flags gate the parent. */
export const canOpenSubItem = (id: string, grants: string[] | null): boolean =>
  grants ? grants.includes(id) : true;

/** The sidebar entry, with only the sub-items this account may open. */
export const visibleMenuEntry = <T extends MenuEntry>(item: T, grants: string[] | null): T =>
  item.subItems
    ? { ...item, subItems: item.subItems.filter((sub) => canOpenSubItem(sub.id, grants)) }
    : item;

/**
 * An entry whose every sub-item was filtered out has nothing left to open —
 * "/contacts" and "/remnutriMeals" are not routes of their own.
 */
export const hasDestination = (item: MenuEntry): boolean =>
  !item.subItems || item.subItems.length > 0;

/** Sidebar entries gated by a role flag, for route guards that only know the flag. */
export const menuIdsForFlag = (flag: keyof AccessControl): string[] =>
  Object.keys(MENU_ACCESS_MAP).filter((id) => MENU_ACCESS_MAP[id] === flag);

/** True when this entry is one the account was explicitly granted. */
const isGrantedMenuItem = (id: string, access: AccessControl | null, grants: string[] | null): boolean => {
  if (grants) return grants.includes(id);
  const flag = MENU_ACCESS_MAP[id];
  if (!flag) return false;
  return access?.[flag] === true;
};

/**
 * The first sidebar destination this account can open.
 *
 * Granted areas are preferred over always-visible ones, in two passes rather
 * than one. A single "first entry the user can open" pass looks right and is
 * not: ABHA, Calendar and User History carry no permission flag, so they are
 * openable by everyone and sit ABOVE Meals in the sidebar. A nutritionist with
 * only the meals flag would land on ABHA — a health-ID screen that has nothing
 * to do with their work — and so would every marketing-, booking- and
 * settings-only account. Only the two areas above ABHA ever landed correctly.
 *
 * So: go to the first area this account was actually given, and fall back to
 * the ungated entries only when it was given none.
 */
export const landingPathFor = (access: AccessControl | null, grants: string[] | null = null): string => {
  const items = (menuItems as MenuEntry[])
    .map((item) => visibleMenuEntry(item, grants))
    .filter(hasDestination);
  const granted = items.find((item) => isGrantedMenuItem(item.id, access, grants));
  if (granted) return menuItemPath(granted);

  const openToAll = items.find((item) => canOpenMenuItem(item.id, access, grants));
  return openToAll ? menuItemPath(openToAll) : FALLBACK_LANDING_PATH;
};
